import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import logo from '../assets/logo-removebg-preview.png';
import manufacturingVideo from '../assets/manufacturing2.mp4';
import { useTranslation } from "react-i18next";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  return (
    <footer style={{ position: 'relative', overflow: 'hidden', fontFamily: "'Outfit', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

        /* ── Video background ── */
        .footer-video {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover; object-position: center;
          pointer-events: none; z-index: 0;
          filter: blur(0px); transform: scale(1.05);
        }
        .footer-overlay {
          position: absolute; inset: 0; z-index: 1; pointer-events: none;
          background: linear-gradient(135deg,
            rgba(6,12,26,0.72) 0%, rgba(10,20,42,0.62) 50%, rgba(6,12,26,0.72) 100%);
        }
        .footer-grid {
          position: absolute; inset: 0; z-index: 1; pointer-events: none;
          background-image: radial-gradient(circle, rgba(180,210,255,0.055) 1px, transparent 1px);
          background-size: 44px 44px;
        }
        .footer-topline {
          position: absolute; top: 0; left: 0; right: 0; height: 2px; z-index: 2;
          background: linear-gradient(90deg,
            transparent 0%, rgba(58,143,163,0.8) 25%,
            rgba(90,171,154,1.0) 50%, rgba(58,143,163,0.8) 75%, transparent 100%);
          box-shadow: 0 0 14px rgba(58,143,163,0.4), 0 0 28px rgba(90,171,154,0.2);
        }

        .footer-content { position: relative; z-index: 3; }

        /* ── Inner & Grid ── */
        .f-inner { max-width: 1280px; margin: 0 auto; padding: 68px 24px 0; }

        .f-cols {
          display: grid;
          grid-template-columns: 1fr;
          gap: 40px;
        }
        @media (min-width: 600px) {
          .f-cols { grid-template-columns: repeat(2, 1fr); gap: 36px 32px; }
        }
        @media (min-width: 1024px) {
          .f-cols { grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 40px; }
        }

        /* ── Logo & tagline ── */
        .f-logo { height: 50px; width: 100px; margin-bottom: 16px; display: block; }
        .f-tagline {
          font-size: .875rem; color: rgba(255,255,255,0.44);
          line-height: 1.75; max-width: 260px;
        }

        /* On mobile, stretch tagline full width */
        @media (max-width: 599px) {
          .f-tagline { max-width: 100%; }
        }

        /* ── Badge ── */
        .f-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(58,143,163,0.12);
          border: 1px solid rgba(58,143,163,0.28);
          border-radius: 999px; padding: 6px 14px; margin-top: 20px;
          backdrop-filter: blur(8px);
        }
        .f-badge-dot {
          width: 7px; height: 7px; border-radius: 50%; background: #5aab9a;
          animation: fpulse 2s ease-in-out infinite;
        }
        @keyframes fpulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:.45} }
        .f-badge-txt { font-size: .72rem; font-weight: 600; letter-spacing: .1em; color: rgba(255,255,255,0.65); }

        /* ── Socials ── */
        .f-socials { display: flex; gap: 10px; margin-top: 22px; flex-wrap: wrap; }
        .f-social {
          width: 38px; height: 38px; border-radius: 10px;
          background: rgba(255,255,255,0.055); border: 1px solid rgba(255,255,255,0.10);
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,0.44); transition: all .3s; text-decoration: none;
        }
        .f-social:hover {
          background: rgba(58,143,163,0.2); border-color: rgba(58,143,163,0.45);
          color: #5aab9a; transform: translateY(-3px);
          box-shadow: 0 6px 18px rgba(58,143,163,0.3);
        }
        .f-social svg { width: 16px; height: 16px; }

        /* ── Column titles ── */
        .f-col-title {
          font-size: .78rem; font-weight: 700; letter-spacing: .12em;
          text-transform: uppercase; color: rgba(255,255,255,0.88);
          margin-bottom: 18px; position: relative; padding-bottom: 10px;
        }
        .f-col-title::after {
          content: ''; position: absolute; bottom: 0; left: 0;
          width: 26px; height: 2px; border-radius: 2px;
          background: linear-gradient(90deg, #3a8fa3, #5aab9a);
        }

        /* ── Links ── */
        .f-links { list-style: none; padding: 0; margin: 0; }
        .f-links li { margin-bottom: 10px; }
        .f-links a {
          font-size: .875rem; color: rgba(255,255,255,0.40);
          text-decoration: none; transition: all .25s;
          display: inline-flex; align-items: center;
        }
        .f-links a::before {
          content: '›'; color: #3a8fa3; opacity: 0; margin-right: 0;
          transform: translateX(-8px); transition: all .25s; font-size: 1rem;
        }
        .f-links a:hover { color: rgba(255,255,255,0.88); padding-left: 6px; }
        .f-links a:hover::before { opacity: 1; transform: translateX(0); margin-right: 4px; }

        /* ── Contact ── */
        .f-contact { list-style: none; padding: 0; margin: 0; }
        .f-contact li { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
        .f-contact-ico {
          width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
          background: rgba(58,143,163,0.14); border: 1px solid rgba(58,143,163,0.25);
          display: flex; align-items: center; justify-content: center;
        }
        .f-contact-ico svg { width: 14px; height: 14px; color: #5aab9a; }
        .f-contact a, .f-contact span {
          font-size: .85rem; color: rgba(255,255,255,0.44);
          text-decoration: none; transition: color .25s; line-height: 1.5; padding-top: 6px;
          word-break: break-all;
        }
        .f-contact a:hover { color: #3a8fa3; }

        /* ── Bottom bar ── */
        .f-bottom-wrap { max-width: 1280px; margin: 52px auto 0; }
        .f-bottom {
          border-top: 1px solid rgba(255,255,255,0.07);
          padding: 22px 24px;
          display: flex; flex-direction: column;
          align-items: center; gap: 12px; text-align: center;
        }
        @media (min-width: 768px) {
          .f-bottom { flex-direction: row; justify-content: space-between; text-align: left; gap: 0; }
        }
        .f-copy { font-size: .8rem; color: rgba(255,255,255,0.26); }
        .f-bot-links { display: flex; gap: 20px; flex-wrap: wrap; justify-content: center; }
        .f-bot-links a {
          font-size: .8rem; color: rgba(255,255,255,0.26);
          text-decoration: none; transition: color .25s;
        }
        .f-bot-links a:hover { color: #3a8fa3; }

        /* ── Mobile tweaks ── */
        @media (max-width: 599px) {
          .f-inner { padding: 48px 20px 0; }
          .f-cols { gap: 32px; }
          .f-col-title { font-size: .75rem; margin-bottom: 14px; }
          .f-links a { font-size: .84rem; }
          .f-contact a, .f-contact span { font-size: .82rem; }
          .f-bottom-wrap { margin-top: 36px; }
          .f-bottom { padding: 18px 20px; gap: 10px; }
          .f-copy { font-size: .76rem; }
        }
      `}</style>

      {/* Video background */}
      <video autoPlay loop muted playsInline className="footer-video" aria-hidden="true">
        <source src={manufacturingVideo} type="video/mp4" />
      </video>
      <div className="footer-overlay" />
      <div className="footer-grid" />
      <div className="footer-topline" />

      <div className="footer-content">
        <div className="f-inner">
          <div className="f-cols">

            {/* ── Company col ── */}
            <div>
              <img src={logo} alt="Curevion Saludis" className="f-logo" />
              <p className="f-tagline">{t("footer.tagline")}</p>
              <div className="f-badge">
                <div className="f-badge-dot" />
                <span className="f-badge-txt">{t("footer.badge")}</span>
              </div>
              <div className="f-socials">
                {[
                  { href: 'https://www.linkedin.com/in/curevion-saludis-36108641a?utm_source=share_via&utm_content=profile&utm_medium=member_ios', icon: <Linkedin /> },
                  { href: 'https://www.instagram.com/curevionsaludis?igsh=aWljMzVqb2kzYjRh&utm_source=qr', icon: <Instagram /> },
                ].map((social, i) => (
                  <a
                    key={i}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="f-social"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* ── Quick Links ── */}
            <div>
              <h3 className="f-col-title">{t("footer.quickLinks")}</h3>
              <ul className="f-links">
                {[
                  ['/',        t('footer.links.home')],
                  ['/about',   t('footer.links.about')],
                  ['/global',  t('globalPage.hero.eyebrow')],
                  ['/quality', t('footer.links.quality')],
                ].map(([p, l]) => (
                  <li key={p}><Link to={p}>{l}</Link></li>
                ))}
              </ul>
            </div>

            {/* ── Products ── */}
            <div>
              <h3 className="f-col-title">{t("footer.products")}</h3>
              <ul className="f-links">
                {[
                  ['/products/oncology',   t('footer.productLinks.oncology')],
                  ['/products/injections', t('footer.productLinks.injections')],
                  ['/products/infusions',  t('footer.productLinks.infusions')],
                ].map(([p, l]) => (
                  <li key={p}><Link to={p}>{l}</Link></li>
                ))}
              </ul>
            </div>

            {/* ── Contact ── */}
            <div>
              <h3 className="f-col-title">{t("footer.contact")}</h3>
              <ul className="f-contact">
                <li>
                  <div className="f-contact-ico"><Mail /></div>
                  <a href="mailto:Info@curevionsaludis.com">Info@curevionsaludis.com</a>
                </li>
                <li>
                  <div className="f-contact-ico">
                    <Phone />
                  </div>

                  <div>
                    <a href="tel:+917669433416">+91 – 7669433416</a>
                    <br/>
                    <a href="tel:+918796969361">+91 – 8796969361</a>
                    <br />
                    <a href="tel:+918796969362">+91 – 8796969362</a>
                  </div>
                </li>
                <li>
                  <div className="f-contact-ico"><MapPin /></div>
                  <span>Rohini, Delhi</span>
                </li>
              </ul>
            </div>

          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="f-bottom-wrap">
          <div className="f-bottom">
            <p className="f-copy">
              © {currentYear} Curevion Saludis. {t("footer.copyright")}
            </p>
            <div className="f-bot-links">
              <Link to="/privacy">{t("footer.privacy")}</Link>
              <Link to="/terms">{t("footer.terms")}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;